import { Link } from "@frontend/lib/link";
import { useState, type ComponentProps, type ReactNode } from "react";
import type { PillTone } from "@creators/backend/lib/collabs/state-machine";
import { formatINR } from "@creators/backend/lib/money";

export function cx(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------

const BUTTON_VARIANTS = {
  primary:
    "bg-brand-600 text-canvas hover:bg-brand-700 focus-visible:ring-brand-100 disabled:bg-brand-600/50",
  // Fixed dark green — theme money-600 lightens in dark mode and white labels
  // disappear on the mint fill.
  money:
    "bg-[#047857] text-white hover:bg-[#065f46] focus-visible:ring-[#047857]/30 disabled:bg-[#047857]/50",
  secondary:
    "bg-surface text-ink border border-line-strong hover:bg-canvas focus-visible:ring-brand-100",
  ghost: "text-ink-muted hover:text-ink hover:bg-canvas focus-visible:ring-brand-100",
  danger:
    "bg-surface text-alert-600 border border-alert-100 hover:bg-alert-50 focus-visible:ring-alert-100",
} as const;

const BUTTON_SIZES = {
  sm: "px-3 py-1.5 text-sm rounded-lg",
  md: "px-4 py-2.5 text-sm rounded-xl",
  lg: "px-5 py-3.5 text-base rounded-xl",
} as const;

type ButtonProps = ComponentProps<"button"> & {
  variant?: keyof typeof BUTTON_VARIANTS;
  size?: keyof typeof BUTTON_SIZES;
  full?: boolean;
};

export function Button({
  variant = "primary",
  size = "md",
  full,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-2 font-medium transition",
        "focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        full && "w-full",
        className,
      )}
    />
  );
}

export function LinkButton({
  variant = "primary",
  size = "md",
  full,
  className,
  ...props
}: ComponentProps<typeof Link> & {
  variant?: keyof typeof BUTTON_VARIANTS;
  size?: keyof typeof BUTTON_SIZES;
  full?: boolean;
}) {
  return (
    <Link
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-2 font-medium transition",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        full && "w-full",
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------------------

const PILL_TONES: Record<PillTone, string> = {
  neutral: "bg-canvas text-ink-muted border-line",
  // Tint from the accent colour rather than a solid mud fill — stays legible
  // in dark mode where warn-50 used to read as a brown slab.
  amber: "border-warn-600/30 bg-warn-600/10 text-warn-600",
  blue: "border-brand-500/25 bg-brand-500/10 text-brand-500",
  green: "border-money-600/30 bg-money-600/10 text-money-600",
  red: "border-alert-600/30 bg-alert-600/10 text-alert-600",
};

export function Pill({
  tone = "neutral",
  children,
  className,
  size = "md",
  /** Colour tone usually shows a leading dot; set false when the child has its own icon. */
  dot,
}: {
  tone?: PillTone;
  children: ReactNode;
  className?: string;
  size?: "sm" | "md";
  dot?: boolean;
}) {
  const showDot = dot ?? tone !== "neutral";
  return (
    <span
      className={cx(
        "inline-flex max-w-full items-center gap-1.5 border font-medium",
        size === "sm"
          ? "rounded px-1.5 py-px text-[10px] leading-tight"
          : "rounded-md px-2 py-0.5 text-xs whitespace-nowrap",
        PILL_TONES[tone],
        className,
      )}
    >
      {showDot ? (
        <span
          className="size-1.5 shrink-0 rounded-full bg-current opacity-80"
          aria-hidden="true"
        />
      ) : null}
      <span className="inline-flex min-w-0 items-center gap-1.5 truncate">{children}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------

export function Card({
  children,
  className,
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "article" | "li";
}) {
  return <Tag className={cx("card", className)}>{children}</Tag>;
}

/**
 * A counterparty's photo, or their monogram when there is none / the image
 * fails to load. Both shapes are the same size so lists do not reflow.
 */
export function Avatar({
  name,
  url,
  size = "md",
  className,
}: {
  name: string;
  url?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const sizing = cx(
    "shrink-0 rounded-full",
    size === "sm" ? "size-7 text-[10px]" : size === "lg" ? "size-11 text-sm" : "size-9 text-xs",
    className,
  );

  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials =
    parts.length === 0
      ? "?"
      : parts.length === 1
        ? parts[0]!.slice(0, 2).toUpperCase()
        : `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();

  if (url && failedUrl !== url) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailedUrl(url)}
        className={cx(sizing, "object-cover ring-1 ring-line")}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cx(
        sizing,
        "inline-flex items-center justify-center bg-canvas font-semibold text-ink-muted ring-1 ring-line",
      )}
    >
      {initials}
    </span>
  );
}

export function SectionHeading({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-4">
      <div>
        <h2 className="text-sm font-semibold tracking-wide text-ink-muted uppercase">{title}</h2>
        {hint ? <p className="mt-0.5 text-sm text-ink-muted">{hint}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Money({
  paise,
  className,
  size = "md",
}: {
  paise: number;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  const sizes = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-xl",
    xl: "text-3xl",
  } as const;

  return (
    <span className={cx("tabular font-semibold", sizes[size], className)}>{formatINR(paise)}</span>
  );
}

export function Field({
  label,
  hint,
  error,
  optional,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="field-label">
        {label}
        {optional ? <span className="font-normal text-ink-subtle"> (optional)</span> : null}
      </span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-xs text-alert-600">{error}</span>
      ) : hint ? (
        <span className="field-hint block">{hint}</span>
      ) : null}
    </label>
  );
}

/**
 * A caption for a *set* of controls — radios, choice cards, toggles. `Field`
 * cannot do this job: its <label> would claim the first control inside it, so
 * clicking the caption would silently pick the first option.
 */
export function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="field-label">{label}</legend>
      {children}
      {hint ? <p className="field-hint">{hint}</p> : null}
    </fieldset>
  );
}

/**
 * An optional input that costs a line until it is wanted. Anything not required
 * to submit the form should not be taking up a box on first sight.
 */
export function OptionalField({
  label,
  addLabel,
  hint,
  children,
}: {
  label: string;
  /** The collapsed line, phrased as the action: "Add a note". */
  addLabel: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <details className="group">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-sm font-medium text-ink-muted transition hover:text-ink [&::-webkit-details-marker]:hidden">
        <span aria-hidden="true" className="text-ink-subtle group-open:rotate-45">
          +
        </span>
        <span className="group-open:hidden">{addLabel}</span>
        <span className="hidden group-open:inline">{label}</span>
      </summary>
      <div className="mt-2">
        {children}
        {hint ? <span className="field-hint block">{hint}</span> : null}
      </div>
    </details>
  );
}

/**
 * The secondary path beside a primary button — declining a payment, asking for
 * a revision, walking away. Folded shut so it never competes with the main
 * action, but shaped like a control rather than a stray sentence.
 */
export function Disclosure({
  label,
  icon,
  tone = "neutral",
  children,
}: {
  label: string;
  icon?: ReactNode;
  tone?: "neutral" | "danger";
  children: ReactNode;
}) {
  return (
    <details className="group">
      <summary
        className={cx(
          "-mx-1.5 flex cursor-pointer list-none items-center gap-2 rounded-lg px-1.5 py-1.5 text-sm font-medium transition [&::-webkit-details-marker]:hidden",
          tone === "danger"
            ? "text-ink-muted hover:bg-alert-50 hover:text-alert-600"
            : "text-ink-muted hover:bg-canvas hover:text-ink",
        )}
      >
        {icon ? <span className="shrink-0">{icon}</span> : null}
        <span className="flex-1">{label}</span>
        <span aria-hidden="true" className="text-ink-subtle transition group-open:rotate-90">
          ›
        </span>
      </summary>
      <div className="mt-2.5">{children}</div>
    </details>
  );
}

const CALLOUT_TONES: Record<
  PillTone,
  { box: string; iconWrap: string; icon: string }
> = {
  // Accent tint + ink type: the colour carries the meaning, the words stay
  // readable. Solid yellow-on-brown fills looked muddy in dark mode.
  neutral: {
    box: "border-line bg-canvas",
    iconWrap: "bg-ink/5 text-ink-muted",
    icon: "text-ink-muted",
  },
  amber: {
    box: "border-warn-600/25 bg-warn-600/[0.08]",
    iconWrap: "bg-warn-600/15 text-warn-600",
    icon: "text-warn-600",
  },
  blue: {
    box: "border-brand-500/25 bg-brand-500/[0.08]",
    iconWrap: "bg-brand-500/15 text-brand-500",
    icon: "text-brand-500",
  },
  green: {
    box: "border-money-600/25 bg-money-600/[0.08]",
    iconWrap: "bg-money-600/15 text-money-600",
    icon: "text-money-600",
  },
  red: {
    box: "border-alert-600/25 bg-alert-600/[0.08]",
    iconWrap: "bg-alert-600/15 text-alert-600",
    icon: "text-alert-600",
  },
};

export function Callout({
  tone = "neutral",
  title,
  children,
  compact = false,
}: {
  tone?: PillTone;
  title?: string;
  children: ReactNode;
  /** Smaller type + padding for tight action panels. */
  compact?: boolean;
}) {
  const t = CALLOUT_TONES[tone];

  return (
    <div
      className={cx(
        "flex rounded-xl border",
        compact ? "gap-2.5 px-3 py-2.5" : "gap-3 px-4 py-3.5",
        t.box,
      )}
    >
      <span
        className={cx(
          "flex shrink-0 items-center justify-center rounded-lg",
          compact ? "size-6 rounded-md" : "size-8",
          t.iconWrap,
        )}
        aria-hidden="true"
      >
        <CalloutIcon tone={tone} className={cx(compact ? "size-3.5" : "size-4", t.icon)} />
      </span>
      <div className={cx("min-w-0 flex-1", compact ? "pt-px" : "pt-0.5")}>
        {title ? (
          <p
            className={cx(
              "font-semibold tracking-tight text-ink",
              compact ? "text-xs" : "text-sm",
            )}
          >
            {title}
          </p>
        ) : null}
        <div
          className={cx(
            "leading-snug text-ink-muted",
            compact ? "text-xs" : "text-sm leading-relaxed",
            title ? (compact ? "mt-0.5" : "mt-1") : compact ? null : "pt-0.5",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function CalloutIcon({
  tone,
  className,
}: {
  tone: PillTone;
  className?: string;
}) {
  if (tone === "green") {
    return (
      <svg viewBox="0 0 16 16" fill="none" className={className}>
        <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M5.25 8.1 7.1 9.9l3.65-3.8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (tone === "red") {
    return (
      <svg viewBox="0 0 16 16" fill="none" className={className}>
        <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M8 5v4.25M8 11.25h.01"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (tone === "amber") {
    return (
      <svg viewBox="0 0 16 16" fill="none" className={className}>
        <path
          d="M8 2.75 14 13.25H2L8 2.75Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M8 6.75v3M8 11.5h.01"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  // blue + neutral — info
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className}>
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 7.25V11M8 5.25h.01"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function EmptyState({
  title,
  children,
  icon,
}: {
  title: string;
  children?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-line-strong px-6 py-12 text-center">
      <span
        className="inline-flex rounded-full bg-canvas p-3 text-ink-subtle/70"
        aria-hidden="true"
      >
        {icon ?? <InboxEmptyIcon className="size-6" />}
      </span>
      <p className="mt-3 font-medium text-ink">{title}</p>
      {children ? <div className="mt-1.5 text-sm text-ink-muted">{children}</div> : null}
    </div>
  );
}

export function InboxEmptyIcon({ className = "size-6" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth={1.5}
      className={cx(className, "stroke-current")}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
      />
    </svg>
  );
}

export function FilterEmptyIcon({ className = "size-6" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth={1.5}
      className={cx(className, "stroke-current")}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
      />
    </svg>
  );
}

export function Divider({ label }: { label?: string }) {
  if (!label) return <hr className="border-line" />;
  return (
    <div className="flex items-center gap-3">
      <hr className="flex-1 border-line" />
      <span className="text-xs font-medium tracking-wide text-ink-subtle uppercase">{label}</span>
      <hr className="flex-1 border-line" />
    </div>
  );
}
